import { type ReportTemplate } from 'reportingSolution/constants'

const config: ReportTemplate = {
  id: 'black_friday_base_report',
  active: true,
  label: 'Black Friday Report',
  category: 'Seasonal',
  description:
    'A report made for tracking performance during Black Week. The report contains data from the same day last year for easy YoY comparison',
  image_url: 'https://lh3.google.com/u/0/d/1zPmELvMA72wdGa8n8XC_Lag2JRH4l5rQ=w2368-h1426-iv1',
  authors: [
    {
      name: 'Tobias Hink',
      email: 'hink@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/a-/AOh14GjyNEc4WJk3HgY11avoS-2opMitZKNbLBZcaWvS=s126-p-k-rw-no'
    },
    {
      name: 'Daniel Olsgaard',
      email: 'olsgaard@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/a/ACg8ocK-Ujf1xW9T-SYaGoLD1TlYkTDjbOr8z7nJnBTSj8b-uLQ=s288-c-no'
    }
  ],
  documentation: {
    type: 'knowledge_hub',
    link: 'https://help.alvie.io/en/articles/174873-black-friday-report-overview',
    link_description: 'Knowledge Hub: Black Friday Report'
  },
  dashboard: {
    template_report_id: 'e166f0a8-9e0c-488e-8ad1-e852cf430992',
    report_name_template: 'copy of Precis Black Friday Report- {today}',
    datasources: [
      {
        alias: 'black_friday',
        datasourceName: 'black_friday - {name}',
        type: 'TABLE',
        tableId: 'black_friday_report_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      }
    ],
    sheets_datasources: []
  },
  sources: {
    ga4: {
      reports: ['black_friday_report'],
      optional: true
    },
    // Kept as optional for existing configs / validation compatibility.
    // Bifrost models are GA4-only and do not read UA tables.
    google_analytics: {
      reports: ['black_friday_report'],
      optional: true
    },
    gads: {
      reports: ['black_friday_report'],
      optional: true
    },
    bing: {
      reports: ['black_friday_report'],
      optional: true
    },
    facebook: {
      reports: ['black_friday_report'],
      optional: true
    },
    tiktok: {
      reports: ['black_friday_report'],
      optional: true
    },
    snapchat: {
      reports: ['black_friday_report'],
      optional: true
    },
    dv360: {
      reports: ['black_friday_report'],
      optional: true
    },
    google_sheets: {
      reports: ['black_friday_report'],
      optional: true
    }
  },
  inputs: {
    ga4: [
      {
        id: 'ga4CustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Analytics 4.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Property ID',
            value: 'property_id',
            type: 'string'
          },
          {
            label: 'Hostname',
            value: 'hostname',
            type: 'string'
          },
          {
            label: 'Campaign',
            value: 'campaign',
            type: 'string'
          },
          {
            label: 'Source',
            value: 'source',
            type: 'string'
          },
          {
            label: 'Medium',
            value: 'medium',
            type: 'string'
          },
          {
            label: 'Source / Medium',
            value: 'source_medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'gads',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / ppc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                }
              ]
            }
          },
          {
            name: 'bing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / ppc'
                }
              ]
            }
          },
          {
            name: 'facebook',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / paidsocial'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'instagram / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'instagram / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'ig / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'ig / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'meta / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'meta / cpm'
                }
              ]
            }
          },
          {
            name: 'tiktok',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / paidsocial'
                }
              ]
            }
          },
          {
            name: 'snapchat',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / paidsocial'
                }
              ]
            }
          },
          {
            name: 'dv360',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'dbm / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'dbm / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'dv360 / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'dv360 / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpm'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'ga4MarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Analytics 4.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Property ID',
            value: 'property_id',
            type: 'string'
          },
          {
            label: 'Hostname',
            value: 'hostname',
            type: 'string'
          },
          {
            label: 'Campaign',
            value: 'campaign',
            type: 'string'
          },
          {
            label: 'Source',
            value: 'source',
            type: 'string'
          },
          {
            label: 'Medium',
            value: 'medium',
            type: 'string'
          },
          {
            label: 'Source / Medium',
            value: 'source_medium',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    gads: [
      {
        id: 'gadsCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'gads',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign__name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'gadsMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    bing: [
      {
        id: 'bingCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'AccountName',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'CampaignName',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'bing',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'CampaignName',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'bingMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'AccountName',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'CampaignName',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    facebook: [
      {
        id: 'facebookCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'facebook',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign_name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'facebookMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],

    tiktok: [
      {
        id: 'tiktokCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in TikTok.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'advertiser_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'tiktok',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign_name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'tiktokMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in TikTok.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'advertiser_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    snapchat: [
      {
        id: 'snapchatCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Snapchat.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'snapchat',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'snapchatMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Snapchat.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    dv360: [
      {
        id: 'dv360CustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Partner name',
            value: 'filter_partner_name',
            type: 'string'
          },
          {
            label: 'Partner ID',
            value: 'filter_partner',
            type: 'string'
          },
          {
            label: 'Advertiser name',
            value: 'filter_advertiser_name',
            type: 'string'
          },
          {
            label: 'Advertiser ID',
            value: 'filter_advertiser',
            type: 'string'
          },
          {
            value: 'filter_media_plan_name',
            label: 'Campaign Name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'filter_media_plan',
            type: 'string'
          },
          {
            label: 'Insertion order name',
            value: 'filter_insertion_order_name',
            type: 'string'
          },
          {
            label: 'Insertion order ID',
            value: 'filter_insertion_order',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'dv360',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'filter_insertion_order_name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'dv360MarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Partner name',
            value: 'filter_partner_name',
            type: 'string'
          },
          {
            label: 'Partner ID',
            value: 'filter_partner',
            type: 'string'
          },
          {
            label: 'Advertiser name',
            value: 'filter_advertiser_name',
            type: 'string'
          },
          {
            label: 'Advertiser ID',
            value: 'filter_advertiser',
            type: 'string'
          },
          {
            value: 'filter_media_plan_name',
            label: 'Campaign Name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'filter_media_plan',
            type: 'string'
          },
          {
            label: 'Insertion order name',
            value: 'filter_insertion_order_name',
            type: 'string'
          },
          {
            label: 'Insertion order ID',
            value: 'filter_insertion_order',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    other: [
      {
        id: 'targetCurrency',
        type: 'dropdown',
        label: 'Target Currency',
        helperText: 'The currency to transform all currency-based columns (cost, revenue, etc) to.',
        required: true,
        defaultValue: 'EUR',
        mutable: true
      }
    ]
  }
}

export default config
